import { Component, Input, OnDestroy } from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as ReportingActions from '@reporting/reporting.actions';
import * as fromReporting from '@reporting/reporting.reducers';

import { GraphFilter, GraphFilterDays, GraphFilterTimePeriod, ReportCurrentMonth, ReportEndValues, ReportItemGraph } from '@shared/models/Reporting';

@Component({
  selector: 'app-reporting-chart',
  templateUrl: './reporting-chart.component.html',
  styleUrls: ['./reporting-chart.component.scss'],
  standalone: false
})
export class ReportingChartComponent implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  @Input() customer: Customer;
  @Input() currentAccount: CustomerAccount;
  currentMonth: ReportCurrentMonth;
  dataChart: ReportItemGraph[];
  daysDifference = 0;
  endValues: ReportEndValues;
  filterConfig: GraphFilter;
  filterOptions = Object.keys(GraphFilterTimePeriod);
  GraphFilterDays = GraphFilterDays;
  GraphFilterTimePeriod = GraphFilterTimePeriod;
  hideDepositsTransfers = false;
  hideWithdrawals = false;
  isInitialised = false;
  lastPerformanceMonth: string;
  transactionsTypeDepositTransfer: ReportItemGraph[];
  transactionsTypeWithdrawal: ReportItemGraph[];

  eventsMap = {
    [GraphFilterTimePeriod.WEEK]: '1_week',
    [GraphFilterTimePeriod.MONTH]: '1_month',
    [GraphFilterTimePeriod.QUARTER]: '3_months',
    [GraphFilterTimePeriod.HALF_YEAR]: '6_months',
    [GraphFilterTimePeriod.YEAR]: '1_year',
    [GraphFilterTimePeriod.MORE_YEAR]: '3_years',
    [GraphFilterTimePeriod.MAX]: 'max'
  };

  constructor(
    private store: Store,
    private translateService: TranslateService,
    private amplitudeService: AmplitudeService
  ) {
    this.store.pipe(select(fromReporting.selectPerformanceGraphData), takeUntil(this.onDestroy$)).subscribe((value) => {
      if (!value || !value.length) {
        this.dataChart = [];
        this.lastPerformanceMonth = '';
        return;
      }
      this.dataChart = value;
      this.getCurrentMonth();
    });
    this.store.pipe(select(fromReporting.selectIsInitialised), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isInitialised = value;
    });
    this.store.pipe(select(fromReporting.selectDaysDifference), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.daysDifference = value;
    });
    this.store.pipe(select(fromReporting.selectPerformanceFilter), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.filterConfig = value;
    });
    this.store.pipe(select(fromReporting.selectTransactionsDepositTransfer), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.transactionsTypeDepositTransfer = value;
    });
    this.store.pipe(select(fromReporting.selectTransactionsWithdrawal), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.transactionsTypeWithdrawal = value;
    });
    this.store.pipe(select(fromReporting.selectCurrentMonthData), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentMonth = value as ReportCurrentMonth;
    });
    this.store.pipe(select(fromReporting.selectEndValues), takeUntil(this.onDestroy$)).subscribe((endValues) => {
      if (!endValues || !endValues.end_date) {
        this.endValues = undefined;
        return;
      }
      this.endValues = endValues;
    });
  }

  getCurrentMonth(): void {
    const lastDate = this.dataChart[this.dataChart.length - 1].date;
    this.lastPerformanceMonth = lastDate.toLocaleString(this.translateService.currentLang.slice(0, 2), { month: 'long' });
  }

  setTimePeriod(timePeriod: string, currentTimePeriod: string): void {
    this.amplitudeService.trackEvent(`Performance Graph Engaged`, {
      performance_chart_time_horizon: this.eventsMap[timePeriod],
      kind: this.currentAccount.kind
    });

    this.amplitudeService.trackEvent(`Time Frame Selected`, {
      performance_chart_time_horizon: this.eventsMap[timePeriod],
      previous_time_horizon: this.eventsMap[currentTimePeriod],
      active_value_view: 'performance',
      kind: this.currentAccount.kind
    });

    this.store.dispatch(
      ReportingActions.changePerformanceFilter({
        payload: {
          ...this.filterConfig,
          timePeriod: timePeriod as GraphFilterTimePeriod
        }
      })
    );
  }

  getNumberType(value: string | number): number {
    return Number(value);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
